import BlockchainNetworkDialog from "@/components/admin-components/blockchain-network/BlockchainNetworkDialog";
import BlockchainNetworkFilters from "@/components/admin-components/blockchain-network/BlockchainNetworkFilters";
import BlockchainNetworkGrid from "@/components/admin-components/blockchain-network/BlockchainNetworkGrid";
import BlockchainNetworkHeader from "@/components/admin-components/blockchain-network/BlockchainNetworkHeader";
import BlockchainNetworkPagination from "@/components/admin-components/blockchain-network/BlockchainNetworkPagination";
import BlockchainNetworkSkeleton from "@/components/admin-components/blockchain-network/BlockchainNetworkSkeleton";
import BlockchainNetworkStats from "@/components/admin-components/blockchain-network/BlockchainNetworkStats";
import BlockchainNetworkToolbar from "@/components/admin-components/blockchain-network/BlockchainNetworkToolbar";
import DeleteBlockchainNetworkDialog from "@/components/admin-components/blockchain-network/DeleteBlockchainNetworkDialog";
import { useDebounce } from "@/components/admin-components/blockchain-network/useDebounce";
import { useBlockchainNetworks } from "@/hooks/use-blockchain-network";
import { useBlockchainNetworkMutations } from "@/hooks/useBlockchainNetworkMutations";
import type {
  BlockchainNetwork,
  BlockchainNetworkFilters as BFilterInterface,
} from "@/interfaces/blockchainNetwork";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const AdminBlockchainNetwork = () => {
  const { loading, networks, query, setQuery, refresh, meta } =
    useBlockchainNetworks();

  const defaultFilters: BFilterInterface = {
    search: "",
    enabled: "ALL",
    networkType: "ALL",
    architecture: "ALL",
    supportsTokens: "ALL",
    sortBy: "priority",
    order: "asc",
  };

  const {
    loading: mutationLoading,
    deleteNetwork,
    enableNetwork,
    disableNetwork,
    enableDeposits,
    disableDeposits,
    enableWithdrawals,
    disableWithdrawals,
  } = useBlockchainNetworkMutations({
    refresh,
  });

  const [dialogOpen, setDialogOpen] = useState(false);

  const [selectedNetwork, setSelectedNetwork] =
    useState<BlockchainNetwork | null>(null);

  const [dialogMode, setDialogMode] = useState<"create" | "edit">("create");

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [networkToDelete, setNetworkToDelete] =
    useState<BlockchainNetwork | null>(null);

  const handleResetFilters = () => {
    setFilters(defaultFilters);
  };

  const [filters, setFilters] = useState(defaultFilters);

  const debouncedSearch = useDebounce(filters.search, 400);

  useEffect(() => {
    setQuery((prev) => ({
      ...prev,

      page: 1,

      search: debouncedSearch.trim() || undefined,

      enabled:
        filters.enabled === "ALL" ? undefined : filters.enabled === "ACTIVE",

      networkType:
        filters.networkType === "ALL" ? undefined : filters.networkType,

      supportsTokens:
        filters.supportsTokens === "ALL"
          ? undefined
          : filters.supportsTokens === "YES",

      isEvm: filters.architecture === "EVM" ? true : undefined,

      isUtxo: filters.architecture === "UTXO" ? true : undefined,

      sortBy: filters.sortBy,

      order: filters.order,
    }));
  }, [
    debouncedSearch,
    filters.enabled,
    filters.networkType,
    filters.architecture,
    filters.supportsTokens,
    filters.sortBy,
    filters.order,
  ]);

  const handleRefresh = async () => {
    await refresh();

    toast.success("Blockchain networks refreshed.");
  };

  const handleCreate = () => {
    setSelectedNetwork(null);

    setDialogMode("create");

    setDialogOpen(true);
  };

  const handleToggleNetwork = async (network: BlockchainNetwork) => {
    if (network.enabled) {
      await disableNetwork(network.id);
    } else {
      await enableNetwork(network.id);
    }
  };

  const handleToggleDeposits = async (network: BlockchainNetwork) => {
    if (network.depositEnabled) {
      await disableDeposits(network.id);
    } else {
      await enableDeposits(network.id);
    }
  };

  const handleToggleWithdrawals = async (network: BlockchainNetwork) => {
    if (network.withdrawEnabled) {
      await disableWithdrawals(network.id);
    } else {
      await enableWithdrawals(network.id);
    }
  };

  const handleDelete = (network: BlockchainNetwork) => {
    setNetworkToDelete(network);

    setDeleteDialogOpen(true);
  };

  const handleEdit = (network: BlockchainNetwork) => {
    setSelectedNetwork(network);

    setDialogMode("edit");

    setDialogOpen(true);
  };

  const confirmDelete = async (network: BlockchainNetwork) => {
    await deleteNetwork(network.id);

    setDeleteDialogOpen(false);

    setNetworkToDelete(null);
  };

  return (
    <div className="space-y-8 p-1 sm:p2">
      <BlockchainNetworkHeader loading={loading} onRefresh={refresh} />

      <BlockchainNetworkStats networks={networks} />

      <BlockchainNetworkFilters query={query} onChange={setQuery} />

      <BlockchainNetworkToolbar
        filters={filters}
        loading={loading}
        onFiltersChange={setFilters}
        onRefresh={refresh}
        onCreate={handleCreate}
        onReset={handleResetFilters}
      />

      {loading ? (
        <BlockchainNetworkSkeleton />
      ) : (
        <BlockchainNetworkGrid
          networks={networks}
          onCreate={handleCreate}
          onEdit={handleEdit}
          // onView={handleView}
          onDelete={handleDelete}
          onToggleEnabled={handleToggleNetwork}
          onToggleDeposits={handleToggleDeposits}
          onToggleWithdrawals={handleToggleWithdrawals}
        />
      )}

      <BlockchainNetworkPagination
        meta={meta}
        onPageChange={(page) =>
          setQuery((prev) => ({
            ...prev,
            page,
          }))
        }
        onLimitChange={(limit) =>
          setQuery((prev) => ({
            ...prev,
            limit,
            page: 1,
          }))
        }
      />

      <BlockchainNetworkDialog
        open={dialogOpen}
        mode={dialogMode}
        network={selectedNetwork}
        onClose={() => setDialogOpen(false)}
        onSuccess={refresh}
      />

      <DeleteBlockchainNetworkDialog
        open={deleteDialogOpen}
        loading={mutationLoading}
        network={networkToDelete}
        onClose={() => {
          setDeleteDialogOpen(false);
          setNetworkToDelete(null);
        }}
        onDelete={confirmDelete}
      />
    </div>
  );
};

export default AdminBlockchainNetwork;
